import {Injectable} from '@angular/core';
import {AuthType, EmployeePermissionType, Role, UserData} from '@npck/coid-frontend-api';
import {AppRouteReuseStrategy, RouterService, SentryService} from '@npck/libs/commons';
import {LocalStorageService, SessionStorageService} from 'ngx-webstorage';
import {Path} from '../path';
import {AUTH_TOKEN, AUTH_TYPE, ESIGN_REVOKE_KEY_NAME, ESIGN_REVOKE_TOKEN, USER_DATA} from './browser-storage.keys';
import {DisputeFilterStateService} from './dispute-filter-state.service';

@Injectable({
    providedIn: 'root',
})
export class AuthHolderService {
    authType: AuthType;
    authToken: string;
    revokeToken: string;
    userData: UserData;

    constructor(
        private localStorageService: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private routerService: RouterService,
        private sentryService: SentryService,
        private appRouteReuseStrategy: AppRouteReuseStrategy,
        private disputeFilterStateService: DisputeFilterStateService,
    ) {
        const token = this.localStorageService.retrieve(AUTH_TOKEN);
        this.revokeToken = this.sessionStorage.retrieve(ESIGN_REVOKE_TOKEN);
        if (token) {
            this.authToken = token;
            this.userData = this.localStorageService.retrieve(USER_DATA);
            this.sentryService.setUser(this.userData?.iinEncrypted);
        }
        this.authType = this.localStorageService.retrieve(AUTH_TYPE);
    }

    public get isUser(): boolean {
        return this.role === Role.USER;
    }

    public get isOrganization(): boolean {
        return [Role.HEAD_OF_ORGANIZATION, Role.ORGANIZATION_EMPLOYEE].includes(this.role);
    }

    public get isModerator(): boolean {
        return this.role === Role.MODERATOR;
    }

    public get isBank(): boolean {
        return this.userData?.bank === true;
    }

    public get hasEmployeesManagementAccess(): boolean {
        const permissions = this.userData?.permissions ?? [];
        return permissions.includes(EmployeePermissionType.SUPERVISOR) ||
            permissions.includes(EmployeePermissionType.EMPLOYEES_MANAGEMENT);
    }

    public get hasServicesManagementAccess(): boolean {
        const permissions = this.userData?.permissions ?? [];
        return permissions.includes(EmployeePermissionType.SUPERVISOR) ||
            permissions.includes(EmployeePermissionType.SERVICES_MANAGEMENT);
    }

    public get hasCoidOpenbankingAccessManagementAccess(): boolean {
        const permissions = this.userData?.permissions ?? [];
        return permissions.includes(EmployeePermissionType.SUPERVISOR) ||
            permissions.includes(EmployeePermissionType.COID_OPENBANKING_ACCESS_MANAGEMENT);
    }

    public get hasBillingAccess(): boolean {
        const permissions = this.userData?.permissions ?? [];
        return permissions.includes(EmployeePermissionType.SUPERVISOR) ||
            permissions.includes(EmployeePermissionType.BILLING_ACCESS);
    }

    public get hasDisputesAccess(): boolean {
        const permissions = this.userData?.permissions ?? [];
        return permissions.includes(EmployeePermissionType.SUPERVISOR) ||
            permissions.includes(EmployeePermissionType.DISPUTES_ACCESS);
    }

    public get isPaymentMetadataFilled(): boolean {
        return this.userData?.paymentMetadataFilled === true;
    }

    public get role(): Role {
        return this.userData?.role;
    }

    public get revokingEsignKeyName(): string {
        return this.sessionStorage.retrieve(ESIGN_REVOKE_KEY_NAME);
    }

    storeAuthToken(token: string): void {
        this.authToken = token;
        this.localStorageService.store(AUTH_TOKEN, token);
    }

    storeRevokeToken(token: string): void {
        this.revokeToken = token;
        this.sessionStorage.store(ESIGN_REVOKE_TOKEN, token);
    }

    clearRevokeToken(): void {
        this.revokeToken = null;
        this.sessionStorage.clear(ESIGN_REVOKE_TOKEN);
    }

    storeAuthType(authType: AuthType): void {
        this.authType = authType;
        this.localStorageService.store(AUTH_TYPE, authType);
    }

    storeUserData(userData: UserData): void {
        this.userData = userData;
        this.localStorageService.store(USER_DATA, userData);
        this.sentryService.setUser(userData.iinEncrypted);
    }

    storeRevokingEsignKeyName(keyName: string): void {
        this.sessionStorage.store(ESIGN_REVOKE_KEY_NAME, keyName);
    }

    logout(): void {
        let path;
        if (this.isUser) {
            path = Path.USER;
        } else if (this.isModerator) {
            path = Path.MODERATOR;
        } else {
            path = Path.ORGANIZATION;
        }
        this.clearData();
        this.routerService.navigate(path);
    }

    private clearData(): void {
        this.localStorageService.clear(AUTH_TOKEN);
        this.localStorageService.clear(AUTH_TYPE);
        this.localStorageService.clear(USER_DATA);
        this.authToken = null;
        this.userData = null;
        this.authType = null;
        this.disputeFilterStateService.clear();
        this.appRouteReuseStrategy.clearCache();
        this.sentryService.resetUser();
    }
}
